import { assign, fromPairs, mapValues, some } from "lodash";
import * as React from "react";
import { ComponentType } from "react";
import { withQueryClient } from "~/Components/QueryProvider";
import { createRoot } from "react-dom/client";
import { OptionsProvider } from "~/Contexts/OptionsContext";

export function react2Angular<Props extends {}>(
    Component: ComponentType<any>,
    bindingNames: (keyof Props)[] | null = null,
    injectNames: string[] = [],
): any {
    const names = bindingNames || (Component.propTypes && (Object.keys(Component.propTypes) as (keyof Props)[])) || [];
    const WrappedComponent = withQueryClient(OptionsProvider);

    return {
        bindings: fromPairs(names.map((o: any) => [o, "<"])),
        controller: [
            "$element",
            ...injectNames,
            class extends NgComponent<Props> {
                root: any;

                static get $$ngIsClass() {
                    return true;
                }

                isDestroyed = false;
                injectedProps: { [name: string]: any };

                constructor(
                    private $element: any,
                    ...injectedProps: any[]
                ) {
                    super();
                    this.injectedProps = {};
                    injectNames.forEach((name, i) => {
                        this.injectedProps[name] = injectedProps[i];
                    });
                    this.root = createRoot($element[0]);
                }

                $onInit() {
                    names.forEach((name: keyof Props) => {
                        // @ts-ignore
                        this.props[name] = this[name];
                    });
                }

                render() {
                    if (!this.isDestroyed) {
                        this.root.render(
                            <WrappedComponent>
                                <Component {...(this as any).props} {...(this.injectedProps as any)} />
                            </WrappedComponent>,
                        );
                    }
                }

                componentWillUnmount() {
                    this.isDestroyed = true;
                    if (this.$element[0] && this.root) {
                        this.root.unmount();
                    }
                }
            },
        ],
    };
}

type OnChanges<T> = {
    [K in keyof T]: any;
};

abstract class NgComponent<Props extends { [k: string]: any } = {}, State extends { [k: string]: any } = {}> {
    private __isFirstRender = true;

    protected state: State = {} as State;
    public props: Partial<Props> = {} as Partial<Props>;

    $onChanges(changes: OnChanges<Partial<Props>>) {
        const oldProps = this.props;

        const newProps = mapValues(changes, "currentValue") as Partial<Props>;
        for (const key in newProps) {
            if (key.startsWith("serialized")) {
                let newKey = key.substring("serialized".length);
                newKey = newKey[0].toLowerCase() + newKey.substring(1);
                try {
                    // @ts-ignore
                    newProps[newKey] = JSON.parse(newProps[key]);
                } catch {
                    console.log(newProps[key]);
                }

                delete newProps[key];
            }
        }
        const nextProps = assign({}, this.props, newProps);

        if (this.__isFirstRender) {
            assign(this, { props: nextProps });
            this.componentWillMount();
            this.render();
            this.__isFirstRender = false;
        } else {
            if (!this.didPropsChange(newProps, oldProps)) return;
            this.componentWillReceiveProps(nextProps);
            const shouldUpdate = this.shouldComponentUpdate(nextProps, this.state);
            assign(this, { props: nextProps });
            if (!shouldUpdate) return;

            this.componentWillUpdate(this.props, this.state);
            this.render();
            this.componentDidUpdate(this.props, this.state);
        }
    }

    $postLink() {
        this.componentDidMount();
    }

    $onDestroy() {
        this.componentWillUnmount();
    }

    protected didPropsChange(newProps: Partial<Props>, oldProps: Partial<Props>): boolean {
        return some(newProps, (v, k) => v !== oldProps[k]);
    }

    componentWillMount(): void {}

    componentDidMount(): void {}

    componentWillReceiveProps(_props: Partial<Props>): void {}

    shouldComponentUpdate(_nextProps: Partial<Props>, _nextState: State): boolean {
        return true;
    }

    componentWillUpdate(_props: Partial<Props>, _state: State): void {}

    componentDidUpdate(_props: Partial<Props>, _state: State): void {}

    componentWillUnmount() {}

    render(): void {}
}
